import type { TranslationNamespace } from '@/types/i18n';

const WORKER_PARSE_THRESHOLD_BYTES = 64_000;
const WORKER_TIMEOUT_MS = 15_000;

type PendingParse = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: number;
};

let parserWorker: Worker | null = null;
let nextRequestId = 1;
const pending = new Map<number, PendingParse>();

function isTranslationNamespace(value: unknown): value is TranslationNamespace {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const queue: unknown[] = [value];
  const visited = new WeakSet<object>();
  let nodes = 0;

  while (queue.length > 0) {
    const current = queue.pop();
    if (!current || typeof current !== 'object' || Array.isArray(current)) return false;
    if (visited.has(current)) return false;
    visited.add(current);
    nodes += 1;
    if (nodes > 100_000) return false;

    for (const child of Object.values(current)) {
      if (typeof child === 'string') continue;
      if (!child || typeof child !== 'object' || Array.isArray(child)) return false;
      queue.push(child);
    }
  }
  return true;
}

function rejectPending(error: Error): void {
  for (const item of pending.values()) {
    window.clearTimeout(item.timeout);
    item.reject(error);
  }
  pending.clear();
  parserWorker?.terminate();
  parserWorker = null;
}

function getParserWorker(): Worker {
  if (parserWorker) return parserWorker;
  const worker = new Worker(new URL('./localeParser.worker.ts', import.meta.url), {
    type: 'module',
    name: 'pf2e-locale-parser',
  });
  worker.onmessage = (event: MessageEvent<{
    id: number;
    ok: boolean;
    value?: unknown;
    error?: string;
  }>) => {
    const item = pending.get(event.data.id);
    if (!item) return;
    pending.delete(event.data.id);
    window.clearTimeout(item.timeout);
    if (event.data.ok) item.resolve(event.data.value);
    else item.reject(new Error(event.data.error || 'Invalid locale JSON'));
  };
  worker.onerror = () => rejectPending(new Error('Locale parser worker failed'));
  parserWorker = worker;
  return worker;
}

async function parseInWorker(payload: string): Promise<unknown> {
  const id = nextRequestId++;
  const worker = getParserWorker();
  return await new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      if (pending.has(id)) {
        rejectPending(new Error('Locale parser worker timed out'));
      }
    }, WORKER_TIMEOUT_MS);
    pending.set(id, { resolve, reject, timeout });
    worker.postMessage({ id, payload });
  });
}

export async function parseLocalePayload(payload: string): Promise<TranslationNamespace> {
  let value: unknown;
  if (
    typeof Worker !== 'undefined'
    // UTF-16 length is a cheap lower-bound proxy; avoid allocating a Blob or
    // encoded copy on the critical path merely to decide where parsing runs.
    && payload.length >= WORKER_PARSE_THRESHOLD_BYTES / 2
  ) {
    try {
      value = await parseInWorker(payload);
    } catch {
      // Worker creation can be blocked by a restrictive browser/CSP. Parsing
      // on the main thread is the compatible recovery path, not a load error.
      value = JSON.parse(payload) as unknown;
    }
  } else {
    value = JSON.parse(payload) as unknown;
  }

  if (!isTranslationNamespace(value)) {
    throw new Error('Locale payload must be an object tree with string leaves');
  }
  return value;
}

export const LOCALE_WORKER_PARSE_THRESHOLD_BYTES = WORKER_PARSE_THRESHOLD_BYTES;
